import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { examAPI, courseAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiTag, FiFileText, FiPrinter, FiCheck, FiX, FiHelpCircle, FiSearch, FiFilter } from 'react-icons/fi';
import Select from 'react-select';

// Custom styles for react-select to match Tailwind design
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#2563eb' : '#9ca3af'
    },
    minHeight: '42px',
    borderRadius: '0.5rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? '#2563eb' 
      : state.isFocused 
      ? '#eff6ff' 
      : 'white',
    color: state.isSelected ? 'white' : '#111827',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#2563eb'
    }
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 9999
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

const Exams = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  // New modals
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  
  const [formData, setFormData] = useState({
    exam: '',
    is_quiz: 0,
    is_live: 0,
    onlinetest: 1,
    exam_from: '',
    exam_to: '',
    auto_publish_date: '',
    duration: '',
    attempt: 1,
    passing_percentage: 33,
    marks: '',
    negative_marks: 0,
    is_active: 1,
    publish_result: 1,
    is_neg_marking: 0,
    is_marks_display: 1,
    is_random_question: 0,
    description: '',
    is_free: 0
  });

  useEffect(() => {
    fetchExams();
  }, [activeFilter]);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const params = activeFilter !== 'all' ? { type: activeFilter } : {};
      const response = await examAPI.getExams(params);
      if (response.data.success) {
        setExams(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching exams');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleAddExam = () => {
    setEditingExam(null);
    setFormData({
      exam: '',
      is_quiz: 0,
      is_live: 0,
      onlinetest: 1,
      exam_from: '',
      exam_to: '',
      auto_publish_date: '',
      duration: '',
      attempt: 1,
      passing_percentage: 33,
      marks: '',
      negative_marks: 0,
      is_active: 1,
      publish_result: 1,
      is_neg_marking: 0,
      is_marks_display: 1,
      is_random_question: 0,
      description: '',
      is_free: 0
    });
    setShowModal(true);
  };

  const handleEditExam = async (examId) => {
    try {
      const response = await examAPI.getExam(examId);
      if (response.data.success) {
        const exam = response.data.data;
        setEditingExam(exam);
        setFormData({
          exam: exam.exam || '',
          is_quiz: exam.is_quiz || 0,
          is_live: exam.is_live || 0,
          onlinetest: exam.onlinetest || 0,
          exam_from: exam.exam_from || '',
          exam_to: exam.exam_to || '',
          auto_publish_date: exam.auto_publish_date || '',
          duration: exam.duration || '',
          attempt: exam.attempt || 1,
          passing_percentage: exam.passing_percentage || 33,
          marks: exam.marks || '',
          negative_marks: exam.negative_marks || 0,
          is_active: exam.is_active || 0,
          publish_result: exam.publish_result || 0,
          is_neg_marking: exam.is_neg_marking || 0,
          is_marks_display: exam.is_marks_display || 0,
          is_random_question: exam.is_random_question || 0,
          description: exam.description || '',
          is_free: exam.is_free || 0
        });
        setShowModal(true);
      }
    } catch (error) {
      toast.error('Error loading exam');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.exam || !formData.duration || !formData.marks) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingExam) {
        const response = await examAPI.updateExam(editingExam.id, formData);
        if (response.data.success) {
          toast.success('Exam updated successfully');
          setShowModal(false);
          fetchExams();
        }
      } else {
        const response = await examAPI.createExam(formData);
        if (response.data.success) {
          toast.success('Exam created successfully');
          setShowModal(false);
          fetchExams();
        }
      }
    } catch (error) {
      toast.error(editingExam ? 'Error updating exam' : 'Error creating exam');
      console.error(error);
    }
  };

  const handleDelete = async (examId, examTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${examTitle}"?`)) {
      return;
    }

    try {
      const response = await examAPI.deleteExam(examId);
      if (response.data.success) {
        toast.success('Exam deleted successfully');
        fetchExams();
      }
    } catch (error) {
      toast.error('Error deleting exam');
      console.error(error);
    }
  };

  const handleToggleStatus = async (examId, currentStatus) => {
    try {
      const response = await examAPI.toggleStatus(examId);
      if (response.data.success) {
        toast.success('Status updated successfully');
        fetchExams();
      }
    } catch (error) {
      toast.error('Error updating status');
      console.error(error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
 
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Handler for Add Questions modal
  const handleOpenAddQuestions = (exam) => {
    setSelectedExam(exam);
    setShowAddQuestionsModal(true);
  };

  // Handler for Assign to Course modal
  const handleOpenAssign = (exam) => {
    setSelectedExam(exam);
    setShowAssignModal(true);
  };

  // Handler for View Questions
  const handleViewQuestions = (examId) => {
    navigate(`/exams/${examId}/questions`);
  };

  const getExamTypes = (exam) => {
    const types = [];
    if (exam.is_live == 1) types.push('Live');
    if (exam.onlinetest == 1) types.push('Online Test');
    if (exam.is_quiz == 1) types.push('Quize');
    return types.join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Online Exam List</h1>
      </div>

      {/* Main Content Box */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Header with Title and Buttons */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Online Exam List</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAddExam}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
              >
                <FiPlus className="mr-2" /> Add Exam
              </button>
              <button
                onClick={() => handleFilterClick('quize')}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  activeFilter === 'quize'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Quize
              </button>
              <button
                onClick={() => handleFilterClick('live')}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  activeFilter === 'live'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Live
              </button>
              <button
                onClick={() => handleFilterClick('onlinetest')}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  activeFilter === 'onlinetest'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Online Test
              </button>
                 <button
                onClick={() => handleFilterClick('1')}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  activeFilter === 'is_free'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Is Free
              </button>
              <button
                onClick={() => handleFilterClick('all')}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  activeFilter === 'all' || activeFilter === ''
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                All
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Attempt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam Publish
                  </th>
                  
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Result Publish
                  </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exams.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      <FiFileText size={48} className="text-gray-300 mb-4 mx-auto" />
                      <p className="text-lg font-medium">No exams found</p>
                    </td>
                  </tr>
                ) : (
                  exams.map((exam) => (
                    <tr key={exam.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{exam.exam}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{getExamTypes(exam)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{exam.questions_count || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{exam.attempt}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(exam.exam_from)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(exam.exam_to)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{exam.duration}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={exam.publish_exam_notification == 1}
                          readOnly
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {exam.publish_result == 1 ? (
                          <input
                            type="checkbox"
                            checked
                            readOnly
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                        ) : (
                          <FiX className="text-red-600" size={20} />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {exam.is_free == 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Free
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Paid
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Assign button */}
                          <button
                            onClick={() => handleOpenAssign(exam)}
                            className="inline-flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Assign to Course"
                          >
                            <FiTag size={16} />
                          </button>
                          
                          {/* Add Questions button */}
                          <button
                            onClick={() => handleOpenAddQuestions(exam)}
                            className="inline-flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Add Questions"
                          >
                            <FiHelpCircle size={16} />
                          </button>
                          
                          {/* Edit button */}
                          <button
                            onClick={() => handleEditExam(exam.id)}
                            className="inline-flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Edit"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          
                          {/* Exam Questions List button */}
                          <button
                            onClick={() => handleViewQuestions(exam.id)}
                            className="inline-flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="View Questions"
                          >
                            <FiFileText size={16} />
                          </button>
                          
                          {/* Print button */}
                          <button
                            className="inline-flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Print Exam"
                          >
                            <FiPrinter size={16} />
                          </button>
                          
                          {/* Delete button */}
                          <button
                            onClick={() => handleDelete(exam.id, exam.exam)}
                            className="inline-flex items-center p-2 text-gray-600 hover:bg-red-100 hover:text-red-600 rounded transition-colors"
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                          
                          {/* Status toggle button */}
                          <button
                            onClick={() => handleToggleStatus(exam.id, exam.is_active)}
                            className={`inline-flex items-center p-2 rounded transition-colors ${
                              exam.is_active == 1
                                ? 'text-green-600 hover:bg-green-100'
                                : 'text-red-600 hover:bg-red-100'
                            }`}
                            title="Change status"
                          >
                            {exam.is_active == 1 ? <FiCheck size={16} /> : <FiX size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingExam ? 'Edit Exam' : 'Add Exam'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Exam Type Checkboxes */}
              <div className="mb-6">
                <div className="flex items-center gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_quiz"
                      checked={formData.is_quiz == 1}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded mr-2"
                    />
                    <span className="text-sm text-gray-700">Quiz</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_live"
                      checked={formData.is_live == 1}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded mr-2"
                    />
                    <span className="text-sm text-gray-700">Live</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="onlinetest"
                      checked={formData.onlinetest == 1}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded mr-2"
                    />
                    <span className="text-sm text-gray-700">Online Test</span>
                  </label>
                     <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_free"
                      checked={formData.is_free == 1}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded mr-2"
                    />
                    <span className="text-sm text-gray-700">Is Free</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  In quiz result will be display to student immediately just after exam submission (descriptive question type will be disabled).
                </p>
              </div>

              {/* Exam Title */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exam Title <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="exam"
                  value={formData.exam}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Date Fields Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam From <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="exam_from"
                    value={formData.exam_from}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam To <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="exam_to"
                    value={formData.exam_to}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto Result Publish Date
                  </label>
                  <input
                    type="datetime-local"
                    name="auto_publish_date"
                    value={formData.auto_publish_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Duration, Attempt, Passing Percentage Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Duration <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="Use 00:00:00 formate"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attempt <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="attempt"
                    min="1"
                    value={formData.attempt}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passing Percentage <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="passing_percentage"
                    min="1"
                    max="100"
                    value={formData.passing_percentage}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Marks and Negative Marks Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marks <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="marks"
                    min="1"
                    value={formData.marks}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Negative Marks <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="negative_marks"
                    max="100"
                    step="any"
                    value={formData.negative_marks}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Additional Checkboxes */}
              <div className="mb-4 flex flex-wrap gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active == 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Publish Exam</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="publish_result"
                    checked={formData.publish_result == 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Publish Result</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_neg_marking"
                    checked={formData.is_neg_marking == 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Negative Marking</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_marks_display"
                    checked={formData.is_marks_display == 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Display marks in Exam</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_random_question"
                    checked={formData.is_random_question == 1}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Random Question Order</span>
                </label>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-600">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  {editingExam ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Questions Modal */}
      <AddQuestionsModal
        show={showAddQuestionsModal}
        exam={selectedExam}
        onClose={() => {
          setShowAddQuestionsModal(false);
          setSelectedExam(null);
          fetchExams();
        }}
      />

      {/* Assign to Course Modal */}
      <AssignToCourseModal
        show={showAssignModal}
        exam={selectedExam}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedExam(null);
        }}
      />
    </div>
  );
};

// Add Questions Modal Component
const AddQuestionsModal = ({ show, exam, onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAllMode, setSelectAllMode] = useState(false); // Track if all questions selected
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  
  // Master data
  const [tradegroups, setTradegroups] = useState([]);
  const [trades, setTrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);

  // Transform data for react-select format
  const tradegroupOptions = useMemo(() => [
    { value: '', label: 'All Categories' },
    ...tradegroups.map(tg => ({ value: tg.id, label: tg.name }))
  ], [tradegroups]);

  const tradeOptions = useMemo(() => [
    { value: '', label: 'All Trades' },
    ...trades.map(t => ({ value: t.id, label: t.name }))
  ], [trades]);

  const subjectOptions = useMemo(() => [
    { value: '', label: 'All Subjects' },
    ...subjects.map(s => ({ value: s.id, label: s.name }))
  ], [subjects]);

  const chapterOptions = useMemo(() => [
    { value: '', label: 'All Chapters' },
    ...chapters.map(c => ({ value: c.id, label: c.name }))
  ], [chapters]);

  const topicOptions = useMemo(() => [
    { value: '', label: 'All Topics' },
    ...topics.map(t => ({ value: t.id, label: t.name }))
  ], [topics]);

  // Get currently selected options
  const selectedTradegroup = useMemo(() => 
    tradegroupOptions.find(opt => opt.value === filters.tradegroup) || tradegroupOptions[0],
    [tradegroupOptions, filters.tradegroup]
  );
  
  const selectedTrade = useMemo(() => 
    tradeOptions.find(opt => opt.value === filters.trade) || tradeOptions[0],
    [tradeOptions, filters.trade]
  );
  
  const selectedSubject = useMemo(() => 
    subjectOptions.find(opt => opt.value === filters.subject) || subjectOptions[0],
    [subjectOptions, filters.subject]
  );

  const selectedChapter = useMemo(() => 
    chapterOptions.find(opt => opt.value === filters.chapter) || chapterOptions[0],
    [chapterOptions, filters.chapter]
  );

  const selectedTopic = useMemo(() => 
    topicOptions.find(opt => opt.value === filters.topic) || topicOptions[0],
    [topicOptions, filters.topic]
  );
  useEffect(() => {
    if (show && exam) {
      fetchTradeGroups();
      fetchQuestions(1);
    }
  }, [show, exam]);

  // Dynamically load cascading data
  useEffect(() => {
    if (filters.tradegroup) {
      fetchTrades(filters.tradegroup);
    } else {
      setTrades([]);
    }
  }, [filters.tradegroup]);

  useEffect(() => {
    if (filters.tradegroup && filters.trade) {
      fetchSubjects(filters.tradegroup, filters.trade);
    } else {
      setSubjects([]);
    }
  }, [filters.tradegroup, filters.trade]);

  useEffect(() => {
    if (filters.tradegroup && filters.trade && filters.subject) {
      fetchChapters(filters.tradegroup, filters.trade, filters.subject);
    } else {
      setChapters([]);
    }
  }, [filters.tradegroup, filters.trade, filters.subject]);

  useEffect(() => {
    if (filters.tradegroup && filters.trade && filters.subject && filters.chapter) {
      fetchTopics(filters.tradegroup, filters.trade, filters.subject, filters.chapter);
    } else {
      setTopics([]);
    }
  }, [filters.tradegroup, filters.trade, filters.subject, filters.chapter]);

  // Re-fetch questions when filters change (like admin panel)
  // Note: searchQuery is NOT included to avoid API calls on every keystroke
  useEffect(() => {
    if (show && exam) {
      fetchQuestions(1); // Reset to page 1 when filters change
      setSelectedQuestions([]); // Clear selections when filter changes
      setSelectAllMode(false); // Reset select all mode
    }
  }, [filters]);

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) setTradegroups(response.data.data);
    } catch (error) {
      console.error('Error fetching trade groups:', error);
    }
  };

  const fetchTrades = async (tradegroupId) => {
    try {
      const response = await masterAPI.getTradesByGroup(tradegroupId);
      if (response.data.success) setTrades(response.data.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const fetchSubjects = async (tradegroup, trade) => {
    try {
      const response = await masterAPI.getSubjectsByTrade(tradegroup, trade);
      if (response.data.success) setSubjects(response.data.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchChapters = async (tradegroup, trade, subject) => {
    try {
      const response = await masterAPI.getChaptersBySubject(tradegroup, trade, subject);
      if (response.data.success) setChapters(response.data.data);
    } catch (error) {
      console.error('Error fetching chapters:', error);
    }
  };

  const fetchTopics = async (tradegroup, trade, subject, chapter) => {
    try {
      const response = await masterAPI.getTopicsByChapter(tradegroup, trade, subject, chapter);
      if (response.data.success) setTopics(response.data.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  const fetchQuestions = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        keywords: searchQuery,
        page: page,
        per_page: 20,
        ...filters
      };
      const response = await examAPI.getQuestionBank(params);
      if (response.data.success) {
        setQuestions(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      toast.error('Error fetching questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestions = async () => {
    if (selectedQuestions.length === 0) {
      toast.warning('Please select at least one question');
      return;
    }

    try {
     
     
      const response = await examAPI.addQuestionsToExam(exam.id, selectedQuestions);
      if (response.data.success) {
        toast.success(response.data.message);
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error adding questions');
    }
  };

  const toggleSelectAll = async () => {
    if (selectAllMode || selectedQuestions.length > 0) {
      // Deselect all
      setSelectedQuestions([]);
      setSelectAllMode(false);
    } else {
      // Select all questions across all pages
      setLoading(true);
      try {
        const params = {
          keywords: searchQuery,
          page: 1,
          per_page: pagination.total || 10000, // Fetch all questions
          ...filters
        };
        const response = await examAPI.getQuestionBank(params);
        if (response.data.success) {
          const allQuestionIds = response.data.data.map(q => q.id);
          setSelectedQuestions(allQuestionIds);
          setSelectAllMode(true);
          toast.success(`Selected all ${allQuestionIds.length} questions`);
        }
      } catch (error) {
        toast.error('Error selecting all questions');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleSelectQuestion = (id) => {
    setSelectedQuestions(prev =>
      prev.includes(id) ? prev.filter(qId => qId !== id) : [...prev, id]
    );
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Questions to Exam</h2>
            <p className="text-sm text-gray-600 mt-1">{exam?.exam}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={24} />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200 space-y-4">
          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchQuestions(1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              />
            </div>
            <button
              onClick={() => fetchQuestions(1)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <FiSearch size={18} />
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Tradegroup Select */}
            <Select
              value={selectedTradegroup}
              onChange={(option) => setFilters({ 
                tradegroup: option?.value || '', 
                trade: '', 
                subject: '',
                chapter: '',
                topic: ''
              })}
              options={tradegroupOptions}
              placeholder="Select Category"
              isClearable
              isSearchable
              className="react-select-container"
              classNamePrefix="react-select"
              styles={customSelectStyles}
              noOptionsMessage={() => "No categories found"}
            />

            {/* Trade Select */}
            <Select
              value={selectedTrade}
              onChange={(option) => setFilters({ 
                ...filters, 
                trade: option?.value || '', 
                subject: '',
                chapter: '',
                topic: ''
              })}
              options={tradeOptions}
              placeholder="Select Trade"
              isClearable
              isSearchable
              isDisabled={!filters.tradegroup}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={customSelectStyles}
              noOptionsMessage={() => "No trades found"}
            />

            {/* Subject Select */}
            <Select
              value={selectedSubject}
              onChange={(option) => setFilters({ 
                ...filters, 
                subject: option?.value || '',
                chapter: '',
                topic: ''
              })}
              options={subjectOptions}
              placeholder="Select Subject"
              isClearable
              isSearchable
              isDisabled={!filters.trade}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={customSelectStyles}
              noOptionsMessage={() => "No subjects found"}
            />

            {/* Chapter Select */}
            <Select
              value={selectedChapter}
              onChange={(option) => setFilters({ 
                ...filters, 
                chapter: option?.value || '',
                topic: ''
              })}
              options={chapterOptions}
              placeholder="Select Chapter"
              isClearable
              isSearchable
              isDisabled={!filters.subject}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={customSelectStyles}
              noOptionsMessage={() => "No chapters found"}
            />

            {/* Topic Select */}
            <Select
              value={selectedTopic}
              onChange={(option) => setFilters({ 
                ...filters, 
                topic: option?.value || ''
              })}
              options={topicOptions}
              placeholder="Select Topic"
              isClearable
              isSearchable
              isDisabled={!filters.chapter}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={customSelectStyles}
              noOptionsMessage={() => "No topics found"}
            />
          </div>

          {selectedQuestions.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm text-blue-900 font-medium">
                {selectAllMode 
                  ? `All ${selectedQuestions.length} questions selected across all pages`
                  : `${selectedQuestions.length} question(s) selected`
                }
              </span>
              {selectAllMode && (
                <button
                  onClick={() => {
                    setSelectedQuestions([]);
                    setSelectAllMode(false);
                  }}
                  className="text-xs text-blue-700 hover:text-blue-900 underline"
                >
                  Clear Selection
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No questions found. Try adjusting your search or filters.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectAllMode || (selectedQuestions.length > 0 && questions.every(q => selectedQuestions.includes(q.id)))}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                        title="Select all questions across all pages"
                      />
                      <span className="text-xs text-gray-500">All</span>
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Q.ID</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Question</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {questions.map((question) => (
                  <tr key={question.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => toggleSelectQuestion(question.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">{question.id}</td>
                    <td className="px-4 py-2 text-sm">{question.question}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && questions.length > 0 && pagination.last_page > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-700">
                Page {pagination.current_page} of {pagination.last_page}
              </span>
              <span className="text-sm text-gray-500">
                ({pagination.total} total questions)
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchQuestions(1)}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              
              <button
                onClick={() => fetchQuestions(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex gap-1">
                {[...Array(pagination.last_page)].map((_, index) => {
                  const pageNum = index + 1;
                  const currentPage = pagination.current_page;
                  
                  // Show first page, last page, current page and 2 pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.last_page ||
                    (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => fetchQuestions(pageNum)}
                        className={`px-3 py-1 text-sm border rounded transition-colors ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 3 ||
                    pageNum === currentPage + 3
                  ) {
                    return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                onClick={() => fetchQuestions(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              
              <button
                onClick={() => fetchQuestions(pagination.last_page)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        )}

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAddQuestions}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            disabled={selectedQuestions.length === 0}
          >
            Add {selectedQuestions.length} Question(s)
          </button>
        </div>
      </div>
    </div>
  );
};

// Assign to Course Modal Component
const AssignToCourseModal = ({ show, exam, onClose }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      fetchCourses();
    }
  }, [show]);

  useEffect(() => {
    if (selectedCourse) {
      fetchFolders();
    } else {
      setFolders([]);
      setSelectedFolder('');
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await courseAPI.getCourses();
      if (response.data.success) {
        setCourses(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await courseAPI.getFolders(selectedCourse);
      if (response.data.success) {
        setFolders(response.data.data || []);
      }
    } catch (error) {
      toast.error('Error fetching folders');
    }
  };

  const handleAssign = async () => {
    if (!selectedCourse || !selectedFolder) {
      toast.error('Please select both course and folder');
      return;
    }

    setSubmitting(true);
    try {
      const response = await examAPI.assignToCourse({
        exam_id: exam.id,
        course_id: selectedCourse,
        folder_id: selectedFolder
      });

      if (response.data.success) {
        toast.success(response.data.message);
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error assigning exam');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Assign to Course</h2>
            <p className="text-sm text-gray-600 mt-1">{exam?.exam}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Course <span className="text-red-600">*</span>
                </label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                >
                  <option value="">Choose a course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.title}</option>
                  ))}
                </select>
              </div>

              {selectedCourse && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Folder <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={selectedFolder}
                    onChange={(e) => setSelectedFolder(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
                  >
                    <option value="">Choose a folder</option>
                    {folders.map(folder => (
                      <option key={folder.folder_id} value={folder.folder_id}>
                        {folder.folders}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
            disabled={!selectedCourse || !selectedFolder || submitting}
          >
            {submitting ? 'Assigning...' : 'Assign to Course'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Exams;
